import React, { useEffect, useRef, useState } from 'react'

import { useParams } from 'react-router-dom'
import PropTypes from 'prop-types'

import { Box, Collapse, IconButton, TableRow } from '@mui/material'

import EditIcon from '@mui/icons-material/Edit'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined'
import CloseIcon from '@mui/icons-material/Close'
import DoneIcon from '@mui/icons-material/Done'
import { css } from '@emotion/react'

import {
  StatisticsRowTableCell,
  StatisticsRowTableCellEdit,
  StatisticsRowTextFieldNameInput,
} from '../../../styles/components/StatisticRowStyle'
import statisticsApi from '../../../api/statistics'
import useHandleError from '../../../api/useHandleError'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { attachClosestEdge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { token } from '@atlaskit/tokens'

import invariant from 'tiny-invariant'
import TableFilters from '../../filters/TableFilters'
import reportApi from '../../../api/table'

const tableRowStatusStyles = {
  idle: css({
    ':hover': {
      background: token('color.background.neutral.subtle.hovered', '#091E420F'),
    },
  }),
  preview: css({
    background: token('elevation.surface.overlay', '#FFF'),
    boxShadow: token('elevation.shadow.overlay', 'none'),
  }),
  dragging: css({
    background: token('color.background.neutral.subtle.hovered', '#091E420F'),
    opacity: 0.5,
  }),
}

/**
 * Because we cannot render arbitrary elements inside of a `<tr />` element,
 * we cannot use the `<DropIndicator />` component for row drags.
 *
 * Instead we use bespoke styles.
 */
const tableRowDropIndicatorStyles = {
  top: css({
    position: 'relative',
    '::after': {
      content: "''",
      position: 'absolute',
      top: -1,
      left: 0,
      height: 2,
      width: '100%',
      background: token('color.border.brand', '#0052CC'),
    },
  }),
  bottom: css({
    position: 'relative',
    '::after': {
      content: "''",
      position: 'absolute',
      bottom: -1,
      left: 0,
      height: 2,
      width: '100%',
      background: token('color.border.brand', '#0052CC'),
    },
  }),
}

// eslint-disable-next-line no-unused-vars
const StatisticRow = ({ row, index, tableRows, setTableRows, isDragDisabled, setIsDragDisabled }) => {
  const { id } = useParams()
  const rowRef = useRef(null)
  const [closestEdge, setClosestEdge] = useState(null)
  const isNewRowBool = row?.isNew || false
  const [inputData, setInputData] = useState({
    name: '',
    count: 0,
    filters: {},
  })
  const [isNewRow, setIsNewRow] = useState(isNewRowBool)
  const [isEditMode, setIsEditMode] = useState(isNewRowBool)
  const [nameValidationError, setNameValidationError] = useState(false)
  const [selectedRow, setSelectedRow] = useState(row?.isNew ? row : null)
  const [filters, setFilters] = useState({})
  const handleError = useHandleError()

  const handleChange = (e) => {
    setInputData({ ...inputData, name: e.target.value })
  }

  /* Open/close current row */
  const handleEdit = ({ currentTarget: { name } }) => {
    const clickedRow = tableRows.find((row) => row.id + '' === name)
    setSelectedRow(clickedRow)
    setIsEditMode(!isEditMode)
    setIsDragDisabled(!isEditMode)
    setInputData({
      name: clickedRow.name,
      count: clickedRow.count,
      filters: clickedRow.filters,
    })
    setFilters(clickedRow.filters)
    setNameValidationError(false)
    setIsNewRow(false)
  }

  const handleSave = async (e) => {
    if (!inputData.name) {
      setNameValidationError(true)
      return
    }

    handleEdit(e)

    setSelectedRow(inputData)

    const copiedRows = [...tableRows]
    const clickedRow = copiedRows.find((row) => row.id === selectedRow.id)

    clickedRow.name = inputData.name
    clickedRow.count = inputData.count
    clickedRow.filters = inputData.filters

    if (selectedRow?.isNew) {
      const newFilter = await statisticsApi.addRowsToTile(id, clickedRow).catch(handleError)
      clickedRow.id = newFilter.data.id

      if (typeof clickedRow.order !== 'number') {
        clickedRow.order = copiedRows.length - 1
      }
    } else {
      statisticsApi.updateRow(row.id, clickedRow).catch(handleError)
    }

    clickedRow.isNew = false

    setTableRows(copiedRows)
  }

  const handleDelete = (e) => {
    setTableRows(tableRows.filter((row) => row.id + '' !== e.currentTarget.name))
    if (!row?.isNew) statisticsApi.deleteRow(row.id).catch(handleError)
  }

  /* Deep copies row with new index */
  const handleCopy = async (e) => {
    const selectedRow = tableRows.find((row) => row.id + '' === e.currentTarget.name)
    const copiedRow = { ...selectedRow }

    const copiedRowData = await statisticsApi
      .addRowsToTile(id, copiedRow)
      .then((data) => data.data)
      .catch(handleError)

    copiedRowData.order = tableRows.length

    setTableRows([...tableRows, copiedRowData])
  }

  const setFilteredCount = async () => {
    let getRowsCount = await reportApi
      .getReports(filters)
      .then((data) => {
        if (data.status === 204) {
          return 0
        }

        return data.data.count
      })
      .catch(handleError)
    setInputData({ ...inputData, count: getRowsCount, filters: filters })
  }

  const didMount = useRef(false)
  useEffect(() => {
    setIsDragDisabled(isEditMode)

    if (didMount.current && selectedRow) {
      setFilteredCount()
    }

    didMount.current = true
  }, [filters])

  const [status, setStatus] = useState('idle')
  useEffect(() => {
    const rowElement = rowRef.current
    invariant(rowElement)
    return combine(
      draggable({
        element: rowElement,
        getInitialData: () => ({
          type: 'table-row',
          id: row.id,
          index: index,
        }),
        onGenerateDragPreview() {
          setStatus('preview')
        },
        onDragStart() {
          setStatus('dragging')
        },
        onDrop() {
          setStatus('idle')
        },
      }),
      dropTargetForElements({
        element: rowElement,
        getData({ input, element }) {
          return attachClosestEdge(
            { id: row.id, index: index },
            {
              input,
              element,
              allowedEdges: ['top', 'bottom'],
            }
          )
        },
        canDrop(args) {
          return args.source.data.type === 'table-row'
        },
        onDrag(args) {
          if (args.source.data.id !== row.id) {
            setClosestEdge(extractClosestEdge(args.self.data))
          }
        },
        onDragLeave() {
          setClosestEdge(null)
        },
        onDrop() {
          setClosestEdge(null)
        },
      })
    )
  }, [row])

  return (
    <>
      <TableRow
        draggable
        sx={[tableRowStatusStyles[status], closestEdge && tableRowDropIndicatorStyles[closestEdge]].filter(Boolean)}
        aria-label={`filter-${index}`}
        ref={rowRef}
        style={{ cursor: 'grab' }}
      >
        <StatisticsRowTableCell component="th" scope="row" align="center">
          {isEditMode ? (
            <StatisticsRowTextFieldNameInput
              onChange={handleChange}
              value={inputData.name}
              name={row.id + ''}
              error={nameValidationError}
              aria-label={`filterName-${index}`}
              inputProps={{ maxLength: 30 }}
            />
          ) : (
            row.name
          )}
        </StatisticsRowTableCell>

        <StatisticsRowTableCell align="center" aria-label={`filteredValue-${index}`}>
          {isEditMode ? <StatisticsRowTextFieldNameInput value={inputData.count} disabled /> : row.count}
        </StatisticsRowTableCell>

        <StatisticsRowTableCell align="center">
          <IconButton
            onClick={isEditMode ? handleSave : handleEdit}
            name={row.id}
            aria-label={isEditMode ? `filterSaveButton-${index}` : `filterEditButton-${index}`}
          >
            {isEditMode ? <DoneIcon /> : <EditIcon />}
          </IconButton>

          <IconButton
            onClick={handleDelete}
            name={row.id}
            aria-label={`filterRemoveButton-${index}`}
            sx={isEditMode && { visibility: 'hidden' }}
          >
            <DeleteForeverIcon />
          </IconButton>

          <IconButton
            onClick={
              /*
            Current row in edit mode => Saves edited data
            New row => Removes new row
            Row without edit mode => Copies row
          */
              isEditMode && !isNewRow ? handleEdit : isEditMode && isNewRow ? handleDelete : handleCopy
            }
            name={row.id}
            aria-label={
              isEditMode && !isNewRow
                ? `filterCloseButton-${index}`
                : isEditMode && isNewRow
                  ? `filterRemoveButton-${index}`
                  : `filterCopyButton-${index}`
            }
          >
            {
              /* Current row in edit mode => Close icon
            New row => Delete icon
            Row without edit mode => Copy icon
          */
              isEditMode && !isNewRow ? (
                <CloseIcon />
              ) : isEditMode && isNewRow ? (
                <DeleteForeverIcon />
              ) : (
                <FileCopyOutlinedIcon />
              )
            }
          </IconButton>
        </StatisticsRowTableCell>
      </TableRow>
      <TableRow>
        <StatisticsRowTableCellEdit colSpan={6}>
          <Collapse in={isEditMode || isNewRow} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <TableFilters setFilters={setFilters} page="statistics" usedFilters={row.filters} />
            </Box>
          </Collapse>
        </StatisticsRowTableCellEdit>
      </TableRow>
    </>
  )
}

StatisticRow.propTypes = {
  row: PropTypes.object,
  index: PropTypes.number,
  tableRows: PropTypes.array,
  setTableRows: PropTypes.func,
  setIsNewRow: PropTypes.bool,
  isDragDisabled: PropTypes.bool,
  setIsDragDisabled: PropTypes.func,
}

export default StatisticRow
